import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ExternalLink, Clock, CheckCircle2, XCircle, Mail } from "lucide-react";
import { API, ROLE_BADGE_STYLES } from "@/constants/roles";
import { PrivateHeader } from "@/components/dashboard/PrivateHeader";
import { TestAssignmentCard } from "@/components/dashboard/TestAssignmentCard";
import { ApplicantChat } from "@/components/hiring/ApplicantChat";

const STATUS_DISPLAY = {
  pending: { label: "Received", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", note: "We've got your application — it enters review shortly." },
  reviewing: { label: "Under Review", style: "bg-amber-500/15 text-amber-300 border-amber-500/30", note: "The team is actively reviewing your application right now." },
  accepted: { label: "Accepted", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", note: "You're in! Refresh — your dashboard should now show the worker experience." },
  rejected: { label: "Not selected", style: "bg-rose-500/15 text-rose-300 border-rose-500/30", note: "Not this time — keep building your portfolio and try again in a future round." },
};

export const ApplicantDashboard = ({ me, theme, onToggleTheme }) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("melto_token");
    axios
      .get(`${API}/my/application`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setApplication(r.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const status = application ? STATUS_DISPLAY[application.status] || STATUS_DISPLAY.pending : null;
  const appliedDate = application
    ? new Date(application.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div data-testid="applicant-dashboard" className="min-h-screen bg-[var(--pv-bg)] text-[var(--pv-text-2)]">
      <PrivateHeader
        title="My dashboard"
        theme={theme}
        onToggleTheme={onToggleTheme}
        badge={<span className="rounded-full bg-sky-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase text-sky-300">Applicant</span>}
      />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-24 text-sm font-medium text-[var(--pv-text-4)]">
            <Loader2 className="h-5 w-5 animate-spin text-cyan-500" /> Loading your application...
          </div>
        ) : notFound ? (
          <div data-testid="applicant-no-application" className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-10 text-center ">
            <Mail className="mx-auto h-10 w-10 text-[var(--pv-text-2)]" />
            <h2 className="mt-4 font-display text-xl font-bold text-[var(--pv-text)]">No application found</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-[var(--pv-text-4)]">
              This account isn't linked to an application. Head to the site and submit the Join the Crew form with this email.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-8  sm:p-10">
              <p className="font-mono-label text-[10px] font-bold uppercase tracking-widest text-cyan-400">Application status</p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <h1 data-testid="applicant-status" className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-xl font-bold sm:text-2xl ${status.style}`}>
                  {application.status === "accepted" ? <CheckCircle2 className="h-6 w-6" /> : application.status === "rejected" ? <XCircle className="h-6 w-6" /> : <Clock className="h-6 w-6" />}
                  {status.label}
                </h1>
              </div>
              <p data-testid="applicant-status-note" className="mt-4 text-sm leading-relaxed text-[var(--pv-text-4)]">{status.note}</p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-[var(--pv-subtle)] p-5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Role</p>
                  <p data-testid="applicant-role" className="mt-2">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${ROLE_BADGE_STYLES[application.role] || ROLE_BADGE_STYLES["General / Multiple Roles"]}`}>
                      {application.role}
                    </span>
                  </p>
                </div>
                <div className="rounded-2xl bg-[var(--pv-subtle)] p-5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Applied</p>
                  <p data-testid="applicant-date" className="mt-2 font-display text-base font-bold text-[var(--pv-text)]">{appliedDate}</p>
                </div>
              </div>
            </div>

            <TestAssignmentCard />

            {["pending", "reviewing"].includes(application.status) && (
              <div className="mt-6">
                <ApplicantChat variant="applicant" />
              </div>
            )}

            <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-8  sm:p-10">
              <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Your application</h2>
              <div className="mt-5 space-y-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Portfolio</p>
                  <a
                    data-testid="applicant-portfolio-link"
                    href={application.portfolio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 hover:text-cyan-300"
                  >
                    <ExternalLink className="h-4 w-4" /> {application.portfolio_url}
                  </a>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Your intro</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[var(--pv-text-4)]">{application.message}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Application updates</p>
                  <p data-testid="applicant-updates" className="mt-1.5 text-sm leading-relaxed text-[var(--pv-text-4)]">
                    Updates appear here automatically — check back anytime. If you're accepted, this same login becomes your worker account and you'll get an email.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};
